/**
 * @param {object} feedItem
 * @returns {boolean}
 */
export function hasCampaignInFeed(widgets) {
  if (!Array.isArray(widgets)) {
    return false;
  }
  return widgets.some((item) => {
    const tracking = item?.tracking ?? item?.widget?.tracking;
    return Boolean(tracking?.campaignId);
  });
}

/**
 * @param {object} feedItem
 * @returns {number|undefined}
 */
function getOrder(feedItem) {
  const { order } = feedItem ?? {};
  return typeof order === "number" ? order : undefined;
}

/**
 * Replaces matching slots in the base feed with campaign-only widgets (matched by `order`).
 * @param {Array<object>|undefined|null} baseWidgets - Full feed from SSR / cache
 * @param {Array<object>|undefined|null} campaignWidgets - Partial feed from `campaignWidgetsOnly=true`
 * @returns {Array<object>}
 */
/**
 * Reads campaign widget slots from desktop (`widgets`) or mobile masthead (`stores[].feed.widgets`) payloads.
 * @param {object|null|undefined} response - Page feed API response
 * @param {string} [storeId] - Mobile store id when response uses `stores`
 * @returns {Array<object>}
 */
export function extractCampaignWidgetsFromFeedResponse(response, storeId) {
  if (!response || typeof response !== "object") {
    return [];
  }
  if (Array.isArray(response.widgets)) {
    return response.widgets.filter(Boolean);
  }
  const stores = Array.isArray(response.stores) ? response.stores : [];
  if (stores.length === 0) {
    return [];
  }
  const matchedStore = storeId
    ? stores.find((store) => store.storeId === storeId)
    : (stores.find((store) => store.isSelected) ?? stores[0]);
  return matchedStore?.feed?.widgets ?? [];
}

/**
 * Resolves the initially selected mobile store from SSR stores (`isSelected`).
 * @param {Array<object>|undefined|null} stores
 * @returns {object|undefined}
 */
export function getInitialMobileStore(stores) {
  if (!Array.isArray(stores) || stores.length === 0) {
    return undefined;
  }
  return stores.find((store) => store.isSelected) ?? stores[0];
}

/**
 *
 * @param {*} baseWidgets
 * @param {*} campaignWidgets
 * @returns
 */
export function mergeCampaignWidgetsById(baseWidgets, campaignWidgets) {
  if (!Array.isArray(baseWidgets) || baseWidgets.length === 0) {
    return Array.isArray(baseWidgets) ? [...baseWidgets] : [];
  }
  if (!Array.isArray(campaignWidgets) || campaignWidgets.length === 0) {
    return [...baseWidgets];
  }

  const campaignByOrder = new Map();
  for (const item of campaignWidgets) {
    const order = getOrder(item);
    if (order !== undefined) {
      campaignByOrder.set(order, item);
    }
  }

  if (campaignByOrder.size === 0) {
    return [...baseWidgets];
  }

  return baseWidgets.map((baseItem) => {
    const order = getOrder(baseItem);
    const campaignItem = order !== undefined ? campaignByOrder.get(order) : undefined;
    if (!campaignItem) {
      return baseItem;
    }

    const campaignWidget =
      campaignItem.widget && typeof campaignItem.widget === "object" ? campaignItem.widget : campaignItem;

    if (!campaignWidget?.widgetType) {
      return baseItem;
    }

    return {
      ...baseItem,
      order,
      tracking: campaignItem.tracking ?? baseItem.tracking,
      widget: campaignWidget,
    };
  });
}
